import { Combobox, Input, InputBase, Text, useCombobox } from '@mantine/core';
import { isCancelled, isExternal, type MinuteRange, type ShiftPlanning } from '@reditelji/shared';
import { useEffect, useState } from 'react';
import type { PoolVolunteer, TimelinePayload } from '../api/types';
import { Chips, GROUP_HEAD, Load, TIER_WORD } from './candidateUi';
import { holderLabel, initials, range, reasonChips, type PieceCandidates, type RankedPerson } from './derive';

const NONE = '__none__';

/**
 * A shift's `Oseba`: everyone who could stand there over the form's times,
 * ranked by the autofill rules (SPEC §1.1B), then the free team places by team
 * (SPEC §1.1J). The holder heads the list and cannot be picked again; somebody
 * who is not coming appears only as that holder (SPEC §1.1K).
 *
 * `openSignal` opens it from elsewhere — "Poišči zamenjavo" on a refusal.
 * `onHover` reports the person under the pointer, for the board's preview.
 */
export function PersonPicker({
  data,
  planning,
  silent,
  groups,
  formRange,
  holderId,
  value,
  onChange,
  onHover,
  openSignal,
  disabled,
}: {
  data: TimelinePayload;
  planning: ShiftPlanning;
  silent: boolean;
  groups: PieceCandidates;
  formRange: MinuteRange;
  holderId: string | null;
  value: string | null;
  onChange: (id: string | null) => void;
  onHover: (id: string | null) => void;
  openSignal: number;
  disabled: boolean;
}) {
  const [query, setQuery] = useState('');
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setQuery('');
      onHover(null);
    },
    onDropdownOpen: () => combobox.focusSearchInput(),
  });
  useEffect(() => {
    if (openSignal > 0 && !disabled) combobox.openDropdown();
    // Only a new signal opens it; the store is stable.
  }, [openSignal]);

  const byId = new Map(data.volunteers.map((v) => [v.id, v]));
  const holder = holderId ? byId.get(holderId) : undefined;
  const picked = value ? byId.get(value) : undefined;
  const changed = value !== holderId;
  const q = query.trim().toLowerCase();
  const matches = (v: PoolVolunteer) => !q || v.name.toLowerCase().includes(q);

  const personOption = (c: RankedPerson) => (
    <Combobox.Option
      key={c.person.id}
      value={c.person.id}
      disabled={c.group === 'busy'}
      active={c.person.id === value}
      onMouseEnter={() => onHover(c.group === 'busy' ? null : c.person.id)}
      className="tl-pick-option"
    >
      <div className="tl-pick-row">
        <span className="tl-cand-name">
          {c.person.id === value ? '✓ ' : ''}
          {c.person.name}
        </span>
      </div>
      <div className="tl-cand-meta">
        <span className="tl-cand-tier">{TIER_WORD[c.tier]}</span>
        <Load beforeMin={c.beforeMin} afterMin={c.afterMin} planning={planning} />
      </div>
      {c.group === 'busy' ? (
        <Chips chips={[{ tone: 'flag', text: 'takrat na drugem mestu' }]} />
      ) : (
        <Chips chips={reasonChips(c, data, planning, silent)} max={3} />
      )}
    </Combobox.Option>
  );

  const sections = (['rec', 'warn', 'flag', 'busy'] as const)
    .map((key) => ({ key, list: groups[key].filter((c) => matches(c.person)) }))
    .filter((s) => s.list.length > 0);
  const teams = data.externalGroups
    .map((g) => ({ group: g, list: groups.teams.filter((t) => t.externalGroupId === g.id && matches(t)) }))
    .filter((g) => g.list.length > 0);

  return (
    <Combobox
      store={combobox}
      readOnly={disabled}
      withinPortal
      position="bottom-start"
      width="target"
      onOptionSubmit={(v) => {
        onChange(v === NONE ? null : v);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          size="xs"
          label="Oseba"
          pointer
          disabled={disabled}
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          onClick={() => {
            if (!disabled) combobox.toggleDropdown();
          }}
          className={changed ? 'tl-pick-target tl-pick-target--changed' : 'tl-pick-target'}
        >
          {picked ? (
            <span className="tl-pick-value">
              <span className={`tl-avatar tl-avatar--sm${isExternal(picked) ? ' tl-avatar--team' : ''}`}>
                {isExternal(picked) ? '▪' : initials(picked.name)}
              </span>
              <span className="tl-pick-name">{isExternal(picked) ? holderLabel(picked) : picked.name}</span>
              {changed && holder ? <span className="tl-pick-was">{holder.name}</span> : null}
            </span>
          ) : (
            <span className="tl-pick-value">
              <Input.Placeholder style={{ color: 'oklch(0.5 0.12 25)' }}>Nezasedeno</Input.Placeholder>
              {changed && holder ? <span className="tl-pick-was">{holder.name}</span> : null}
            </span>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          placeholder="Išči po imenu…"
        />
        <Text size="xs" c="dimmed" px={8} pt={4}>
          Razvrščeno po pravilih predloga za {range(formRange.fromMin, formRange.toMin)}.
        </Text>
        <Combobox.Options mah={380} style={{ overflowY: 'auto' }}>
          {holder ? (
            <Combobox.Option value={holder.id} disabled className="tl-pick-option">
              <div className="tl-pick-row">
                <span className="tl-cand-name">{isExternal(holder) ? holderLabel(holder) : holder.name}</span>
                <span className="tl-cand-tier">{isCancelled(holder) ? 'ne pride' : 'trenutno'}</span>
              </div>
            </Combobox.Option>
          ) : null}
          {holderId !== null || value !== null ? (
            <Combobox.Option value={NONE} className="tl-pick-option" onMouseEnter={() => onHover(null)}>
              <div className="tl-pick-row">
                <span className="tl-cand-name" style={{ color: 'oklch(0.5 0.12 25)' }}>
                  Nezasedeno
                </span>
                <span className="tl-cand-tier">sprosti izmeno</span>
              </div>
            </Combobox.Option>
          ) : null}
          {sections.map((s) => (
            <Combobox.Group
              key={s.key}
              label={
                <span className="tl-pick-group">
                  <span className="tl-pool-group-dot" style={{ background: GROUP_HEAD[s.key].dot }} />
                  {GROUP_HEAD[s.key].label}
                </span>
              }
            >
              {s.list.map(personOption)}
            </Combobox.Group>
          ))}
          {teams.map(({ group, list }) => (
            <Combobox.Group key={group.id} label={group.name}>
              {list.map((t) => (
                <Combobox.Option
                  key={t.id}
                  value={t.id}
                  active={t.id === value}
                  className="tl-pick-option"
                  onMouseEnter={() => onHover(t.id)}
                >
                  <div className="tl-pick-row">
                    <span className="tl-cand-name">
                      {t.id === value ? '✓ ' : ''}▪ {holderLabel(t)}
                    </span>
                    <span className="tl-cand-tier">ekipa</span>
                  </div>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          ))}
          {sections.length === 0 && teams.length === 0 ? <Combobox.Empty>Ni zadetkov.</Combobox.Empty> : null}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
